// frontend/src/components/audit/ExpandedRow.tsx
import {
  Stack, Group, Text, Code, Badge, Button, CopyButton, Box,
} from '@mantine/core'
import { SessaoSsm } from './SessaoSsm'
import { IconCopy, IconCheck, IconLink } from '@tabler/icons-react'
import ReactDiffViewer from 'react-diff-viewer-continued'

export interface AuditEventDetail {
  id?: number
  request_id?: string | null
  http_method?: string | null
  http_path?: string | null
  http_status?: number | null
  duration_ms?: number | null
  before_state?: Record<string, unknown> | null
  after_state?: Record<string, unknown> | null
  payload?: Record<string, unknown>
  // A tabela ja recebe os dois da API; faltavam no tipo deste componente,
  // porque ate agora ele so mostrava campos genericos.
  action?: string | null
  resource_type?: string | null
  prev_event_hash?: string | null
  event_hash?: string | null
  client_ip?: string | null
  user_agent?: string | null
}

function pretty(o: unknown): string {
  if (o == null) return '(vazio)'
  return JSON.stringify(o, null, 2)
}

export function ExpandedRow({ event }: { event: AuditEventDetail }) {
  const hasDiff = event.before_state != null || event.after_state != null
  const permalink = `${window.location.origin}${window.location.pathname}?event_id=${event.id}`

  return (
    <Box p="md" style={{ background: 'var(--ai-bg-elevated)' }}>
      <Stack gap="sm">
        <Group>
          <Badge variant="light">Request-ID</Badge>
          <Code>{event.request_id ?? '(legacy)'}</Code>
          {event.request_id && (
            <CopyButton value={event.request_id}>
              {({ copied, copy }) => (
                <Button size="xs" variant="subtle" leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />} onClick={copy}>
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
              )}
            </CopyButton>
          )}
        </Group>

        <Group>
          <Badge variant="light">HTTP</Badge>
          <Text size="sm">
            {event.http_method ?? '?'} {event.http_path ?? '?'} → {event.http_status ?? '?'}
            {event.duration_ms != null && ` (${event.duration_ms}ms)`}
          </Text>
        </Group>

        {hasDiff && (
          <Box>
            <Badge variant="light" mb={4}>Diff before / after</Badge>
            <ReactDiffViewer
              oldValue={pretty(event.before_state)}
              newValue={pretty(event.after_state)}
              splitView
              hideLineNumbers={false}
              showDiffOnly={true}
            />
          </Box>
        )}

        {/* Detalhe por TIPO de recurso, antes do payload cru. Sessao de shell
            e o unico caso hoje; o payload cru continua abaixo para todos, e
            nada muda para os demais tipos. */}
        {event.resource_type === 'ssm_session' && event.payload && (
          <Box>
            <Badge variant="light" mb={4}>Sessão de shell</Badge>
            <SessaoSsm
              payload={event.payload}
              acao={event.action ?? ''}
              tenantId={Number(event.payload.tenant_id ?? 0)}
            />
          </Box>
        )}

        <Box>
          <Badge variant="light" mb={4}>Payload</Badge>
          <Code block>{pretty(event.payload)}</Code>
        </Box>

        <Box>
          <Badge variant="light" mb={4}>Hash chain</Badge>
          <Text size="xs" ff="monospace">prev: {event.prev_event_hash ?? '(legacy)'}</Text>
          <Text size="xs" ff="monospace">curr: {event.event_hash ?? '(legacy)'}</Text>
        </Box>

        <Group>
          <CopyButton value={JSON.stringify(event, null, 2)}>
            {({ copied, copy }) => (
              <Button size="xs" leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />} onClick={copy}>
                {copied ? 'Copiado' : 'Copiar como JSON'}
              </Button>
            )}
          </CopyButton>
          <CopyButton value={permalink}>
            {({ copied, copy }) => (
              <Button size="xs" variant="subtle" leftSection={<IconLink size={14} />} onClick={copy}>
                {copied ? 'Link copiado' : 'Link permanente'}
              </Button>
            )}
          </CopyButton>
        </Group>
      </Stack>
    </Box>
  )
}
